import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import moment from 'moment';
import styles from './css/index.module.scss';
import PageTitle from '../../components/PageTitle';
import MapView from './components/MapView';
import sendRequest from '../../helpers/sendRequest';
import Api from '../../Api';
import IconButton from '../../components/IconButton';
import CancelEditingIcon from '../../assets/formIcons/cancelEditing.svg';
import ChangePhotosIcon from '../../assets/formIcons/photosSmall.svg';
import ChangeOrgIcon from '../../assets/formIcons/changeOrg.svg';
import { nonHourDayTypes } from '../AddOrganization/components/DaysSection';
import { resolvePhotoUrl } from '../../helpers/resolvePhotoUrl';

function Field({ label, value, grey = false, multiline = false, wide = false }) {
  const displayValue = value != null && String(value).trim() !== '' ? value : '—';

  return (
    <div className={classNames(styles.field, { [styles.fieldWide]: wide })}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={classNames(styles.fieldValue, { [styles.textGray]: grey, [styles.multiline]: multiline })}>
        {displayValue}
      </span>
    </div>
  );
}

function formatStatus(isActive) {
  if (isActive === true) return 'Активна';
  if (isActive === false) return 'Забанена';
  return '—';
}

const WEEKDAYS = [
  { key: 'monday', label: 'Понедельник' },
  { key: 'tuesday', label: 'Вторник' },
  { key: 'wednesday', label: 'Среда' },
  { key: 'thursday', label: 'Четверг' },
  { key: 'friday', label: 'Пятница' },
  { key: 'saturday', label: 'Суббота' },
  { key: 'sunday', label: 'Воскресенье' },
];

function ViewSingleOrganization() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('id');

  const [gettingLoading, setGettingLoading] = useState(true);
  const [gettingError, setGettingError] = useState('');
  const [singleOrganization, setSingleOrganization] = useState(null);

  const isOnlineWorking = useMemo(
    () => Boolean(singleOrganization?.category?.is_online),
    [singleOrganization],
  );

  const allPhotos = useMemo(() => {
    if (!singleOrganization) return [];
    return [
      ...(singleOrganization.carousel_photos || []),
      ...(singleOrganization.owner_photos || []),
    ];
  }, [singleOrganization]);

  const onCancelClick = () => {
    navigate('/organizations/view');
  };

  const onEditPhotoClick = () => {
    navigate(`/organizations/edit-photos?id=${singleOrganization._id}`);
  };

  const onEditClick = () => {
    navigate(`/organizations/edit?id=${singleOrganization._id}`);
  };

  const initData = async () => {
    const data = await sendRequest({
      request: Api.getOrganization,
      payload: { id: organizationId },
      warnErrorText: 'while getting single organization',
      setLoading: setGettingLoading,
    });

    if (data?.status !== 'ok') {
      setGettingError(typeof data.message === 'string' ? data.message : 'Ошибка');
    }

    setSingleOrganization(data.organization);
  };

  useEffect(() => {
    initData().then();
  }, []);

  return (
    <div className={styles.wrapper}>
      <PageTitle
        text="Просмотр организации"
        RightComponent={(
          <div className={styles.buttonsRow}>
            <IconButton
              svgPath={CancelEditingIcon}
              tooltipText="Отменить"
              onClick={onCancelClick}
            />
            <IconButton
              svgPath={ChangePhotosIcon}
              tooltipText="Изменить фото"
              onClick={onEditPhotoClick}
            />
            <IconButton
              svgPath={ChangeOrgIcon}
              tooltipText="Редактировать"
              onClick={onEditClick}
            />
          </div>
        )}
      />
      {gettingLoading
        ? <p className={styles.infoText}>Загрузка</p>
        : gettingError
          ? <p className={styles.infoText}>{gettingError}</p>
          : singleOrganization ? (
            <div className={styles.container}>
              <div className={styles.column}>
                <div className={styles.infoBlock}>
                  <p className={styles.blockTitle}>Основное</p>
                  <div className={styles.grid}>
                    <Field label="Название" value={singleOrganization.name} wide />
                    <Field label="Статус" value={formatStatus(singleOrganization.is_active)} />
                    <Field label="Дата создания" value={moment(singleOrganization.createdAt).format('DD.MM.YYYY HH:mm')} />
                    <Field label="ID организации" value={singleOrganization._id} grey />
                    <Field label="Описание" value={singleOrganization.description} multiline wide />
                  </div>
                </div>

                <div className={styles.infoBlock}>
                  <p className={styles.blockTitle}>Владелец</p>
                  <div className={styles.grid}>
                    <Field label="Email" value={singleOrganization.user?.email} />
                    <Field label="Дата регистрации" value={singleOrganization.user?.createdAt ? moment(singleOrganization.user.createdAt).format('DD.MM.YYYY') : '—'} />
                    <Field label="ID владельца" value={singleOrganization.user?._id} grey />
                  </div>
                </div>

                <div className={styles.infoBlock}>
                  <p className={styles.blockTitle}>Категории</p>
                  <div className={styles.grid}>
                    <Field label="Категория" value={singleOrganization.category?.name} />
                    <Field label="Специализация" value={singleOrganization.specialization} />
                    <Field
                      label="Подкатегории"
                      value={singleOrganization.sub_categories?.map((item) => item.name).join(', ')}
                      wide
                    />
                  </div>
                </div>
              </div>

              <div className={styles.column}>
                <div className={styles.infoBlock}>
                  <p className={styles.blockTitle}>Местоположение</p>
                  <div className={styles.grid}>
                    <Field label="Страна" value={singleOrganization.country?.name} />
                    <Field label="Город" value={singleOrganization.city?.name} />
                    <Field
                      label="Дополнительные города"
                      value={singleOrganization.additional_cities?.map((item) => item.name).join(', ')}
                      wide
                    />
                    {!isOnlineWorking && (
                      <Field label="Адрес" value={singleOrganization.address} wide />
                    )}
                  </div>

                  {!isOnlineWorking && singleOrganization.city?._id && singleOrganization.coordinates && (
                    <>
                      <div className={styles.mapWrap}>
                        <MapView
                          city={singleOrganization.city}
                          coordinates={singleOrganization.coordinates}
                          setCoordinates={() => {}}
                          defaultZoom={12}
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className={styles.infoBlock}>
                  <p className={styles.blockTitle}>Контакты</p>
                  <div className={styles.grid}>
                    <Field label="Телефон" value={singleOrganization.contacts?.phone_number} />
                    <Field label="Instagram" value={singleOrganization.contacts?.instagram} />
                    <Field label="Telegram" value={singleOrganization.contacts?.telegram} />
                    <Field label="WhatsApp" value={singleOrganization.contacts?.whatsapp} />
                    <Field label="Viber" value={singleOrganization.contacts?.viber} />
                    <Field label="Facebook" value={singleOrganization.contacts?.facebook} />
                    <Field label="Сайт" value={singleOrganization.contacts?.website} wide />
                  </div>
                </div>

                <div className={styles.infoBlock}>
                  <p className={styles.blockTitle}>Режим работы</p>
                  <div className={styles.schedule}>
                    {WEEKDAYS.map(({ key, label }) => {
                      const value = singleOrganization.work_time?.[key];
                      const preset = nonHourDayTypes[value];
                      return (
                        <div key={key} className={styles.scheduleRow}>
                          <span className={styles.fieldLabel}>{label}</span>
                          <span
                            className={styles.fieldValue}
                            style={{ color: preset?.color }}
                          >
                            {preset?.text || value || '—'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className={styles.column}>
                <div className={styles.infoBlock}>
                  <p className={styles.blockTitle}>Фото</p>
                  {allPhotos.length ? (
                    <div className={styles.list}>
                      {allPhotos.map((photo, index) => {
                        const src = resolvePhotoUrl(photo, Api.url);
                        if (!src) return null;
                        return (
                          <img
                            className={styles.photo}
                            key={photo._id || index}
                            src={src}
                            alt="Фото организации"
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <p className={styles.emptyText}>Нет фото</p>
                  )}
                </div>

                <div className={styles.infoBlock}>
                  <p className={styles.blockTitle}>Тэги</p>
                  {singleOrganization.tags?.length ? (
                    <div className={styles.list}>
                      {singleOrganization.tags.map((tag, index) => (
                        <p key={index} className={styles.tag}>{tag}</p>
                      ))}
                    </div>
                  ) : (
                    <p className={styles.emptyText}>—</p>
                  )}
                </div>

                <div className={styles.infoBlock}>
                  <p className={styles.blockTitle}>Языки</p>
                  {singleOrganization.languages?.length ? (
                    <div className={styles.list}>
                      {singleOrganization.languages.map((language) => (
                        <img
                          className={styles.language}
                          key={language._id}
                          src={resolvePhotoUrl({ url: language.flag_url }, Api.url)}
                          alt={language.name || 'Язык'}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className={styles.emptyText}>—</p>
                  )}
                </div>
              </div>
            </div>
          ) : null}
    </div>
  );
}

export default ViewSingleOrganization;
